import { useState } from 'react';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Button } from '@/components/ui/Button';
import { cleanCode, type CodeProof } from './api';

/**
 * The "enter the 6-digit code" step, with a switch to a recovery code. One
 * input (not six boxes), so a phone's one-time-code autofill and paste both
 * work. Used at sign-in and before any change to your own two-step setting.
 */
export function CodeEntry({
  onSubmit, pending, error, submitLabel = 'Verify', allowRecovery = true, autoFocus = true,
}: {
  onSubmit: (proof: CodeProof) => void;
  pending?: boolean;
  error?: string | null;
  submitLabel?: string;
  allowRecovery?: boolean;
  autoFocus?: boolean;
}) {
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState('');
  const ready = useRecovery ? recovery.trim().length >= 10 : code.length === 6;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready || pending) return;
        onSubmit(useRecovery ? { recovery_code: recovery.trim() } : { code });
      }}
    >
      {useRecovery ? (
        <FormField label="Recovery code" hint="One of the codes you saved when you set up two-step login. Each works once." error={error ?? undefined}>
          <Input value={recovery} onChange={(e) => setRecovery(e.target.value)} autoFocus={autoFocus}
            autoComplete="off" spellCheck={false} placeholder="xxxxx-xxxxx" className="font-mono" />
        </FormField>
      ) : (
        <FormField label="6-digit code" hint="Open your authenticator app and type the code shown for this account." error={error ?? undefined}>
          <Input value={code} onChange={(e) => setCode(cleanCode(e.target.value))} autoFocus={autoFocus}
            inputMode="numeric" autoComplete="one-time-code" placeholder="123456"
            className="text-center font-mono text-lg tracking-[0.35em]" aria-label="6-digit code" />
        </FormField>
      )}
      <Button type="submit" disabled={!ready} loading={pending}>{submitLabel}</Button>
      {allowRecovery && (
        <Button type="button" variant="link" className="self-center text-xs" onClick={() => setUseRecovery((v) => !v)}>
          {useRecovery ? 'Use the code from my app instead' : 'Lost your phone? Use a recovery code'}
        </Button>
      )}
    </form>
  );
}
